/* eslint-disable tailwindcss/no-arbitrary-value */

'use client';

import { Option, Tooltip } from '@material-tailwind/react';
import { LeadCapturePage_attributeList } from '__generated__/graphql';
import { getVehicleDomainService } from 'car-insurance/api/client/clientDomainServices';
import { VehicleModel } from 'car-insurance/types';
import classNames from 'classnames';
import { useClient } from 'hooks/useClient';
import React, { useCallback } from 'react';
import { t } from 'utils/locale';
import { Translated } from 'utils/staticText';
import { track } from 'utils/tracking';
import { EmploymentStatus, ExistingCreditCard } from '../../../lib/const';
import {
  trackApplyLeadFormInteract,
  trackApplyLeadFormSubmit,
  trackQuitLeadDetailsOverlay,
} from '../../../lib/tracking';
import { ITrackingProductPageBaseParameters } from '../../../lib/tracking/shared/baseParameters';
import type { StickyQuickApplyTextType } from '../i18n';
import { isBbbShortFormProduct } from '../utils';
import { useLoanAmountMask, useMonthlyIncomeMask } from './monthlyIncomeMask';
import { Provider } from '$/(application)/apply/[productId]/components/Page';
import { ClippedText } from '$/components/ClippedText';
import { ClippedTextStaticText } from '$/components/ClippedText/i18n';
import Info from '$/components/icons/Info';
import { LoaderForm, SubmitButton } from '$/components/LoaderForm';
import { useClientContext } from '$/modules/context/client';
import { parseForm } from '$/modules/form';
import { AlphaRadio } from '$/modules/material-tailwind/AlphaRadio';
import { AlphaSelect } from '$/modules/material-tailwind/AlphaSelect';
import AlphaSelectMultiple from '$/modules/material-tailwind/AlphaSelectMultiple';
import { AlphaTextInput } from '$/modules/material-tailwind/AlphaTextInput';
import { CountryCode } from '$/shared/localization';

export interface AuthFormValues {
  phoneNumber: string;
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  hasExistingCC: ExistingCreditCard;
  CCTenure: string;
  bankIssued: string[];
  loanAmount: number;
  loanTenure: string;
  employmentStatus: EmploymentStatus;
  employmentTenure: string;
  monthlyIncome: number;
  marketingConsent: boolean;
  dateOfBirth: string;
  businessOperatingDuration: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleUnitPrice: number;
  loanTerm: string;
}

export interface AuthFormProps {
  staticText: Translated<StickyQuickApplyTextType>;
  isOpen: boolean;
  setOpen: (value: boolean) => void;
  marketingConsent: boolean;
  onChangeMarketingConsent: (newValue: boolean) => void;
  onSubmit: (values: AuthFormValues) => void | Promise<void>;
  currentCountryCode: CountryCode;
  currencyDisplay: string;
  tracking: ITrackingProductPageBaseParameters;
  clippedTextStaticText: Translated<typeof ClippedTextStaticText>;
  optionalFieldHidden?: {
    monthlyIncome?: boolean;
    employmentStatus?: boolean;
    hasExistingCC?: boolean;
  };
  productUid?: string;
  attributes: LeadCapturePage_attributeList['attributeList'];
  providers?: Provider[];
  vehiclesData?: {
    makeNames: string[];
    models: VehicleModel[];
  };
  hasReward?: boolean;
}

const FORM_FIELD_NAME_TO_PARAM_MAP: Partial<
  Record<keyof AuthFormValues, string>
> = {
  monthlyIncome: 'monthly_income',
  employmentStatus: 'employment_status',
  hasExistingCC: 'has_credit_card',
  loanAmount: 'loan_amount',
  loanTenure: 'loan_tenure',
  vehicleMake: 'vehicle_make',
  vehicleModel: 'vehicle_model',
  vehicleUnitPrice: 'vehicle_unit_price',
  loanTerm: 'loan_term',
};

const BASE: Partial<Record<keyof AuthFormValues, string>> = {
  monthlyIncome: 'monthly_income',
  employmentStatus: 'employment_status',
};

const CREDIT_CARD_INFO = {
  hasExistingCC: 'has_credit_card',
  CCTenure: 'cc_tenure',
  bankIssued: 'bank_issued',
};

const LOAN_INFO = {
  loanAmount: 'loan_amount',
  loanTenure: 'loan_tenure',
};

const CAR_INFO = {
  vehicleMake: 'vehicle_make',
  vehicleModel: 'vehicle_model',
  vehicleUnitPrice: 'vehicle_unit_price',
  loanTerm: 'loan_term',
};

const FORM_FIELD_CC = {
  ...BASE,
  ...CREDIT_CARD_INFO,
};

const FORM_FIELD_PL = {
  ...BASE,
  ...CREDIT_CARD_INFO,
  ...LOAN_INFO,
};

const FORM_FIELD_LFI = {
  ...BASE,
  employmentTenure: 'employment_tenure',
  dateOfBirth: 'date_of_birth',
};

const FORM_FIELD_BL: Partial<Record<keyof AuthFormValues, string>> = {
  ...CREDIT_CARD_INFO,
  ...LOAN_INFO,
  businessOperatingDuration: 'business_operating_duration',
};

const FORM_FIELD_CL = {
  ...BASE,
  ...CREDIT_CARD_INFO,
  ...CAR_INFO,
};

const FORM_FIELD_ALTL = {
  ...BASE,
  ...CREDIT_CARD_INFO,
  ...LOAN_INFO,
};

export const EXISTING_CC_VALUE_TO_LABEL: Record<ExistingCreditCard, string> = {
  [ExistingCreditCard.No]: 'No',
  [ExistingCreditCard.Yes]: 'Yes',
  [ExistingCreditCard.LessThenSixMonth]: 'Yes, less than 6 months',
  [ExistingCreditCard.FromSixMonthToOneYear]: 'Yes, 6 months to 1 year',
  [ExistingCreditCard.OverOneYear]: 'Yes, over 1 year',
};

const EMPLOYMENT_STATUS_VALUES = [
  EmploymentStatus.EmployedPrivateSector,
  EmploymentStatus.EmployedGovernment,
  EmploymentStatus.EmployedBPO,
  EmploymentStatus.EmployedOFW,
  EmploymentStatus.SelfEmployed,
  EmploymentStatus.ForeignResident,
  EmploymentStatus.Unemployed,
  EmploymentStatus.Retired,
];

export const EMPLOYMENT_STATUS_VALUE_TO_LABEL: Record<
  EmploymentStatus,
  string
> = {
  [EmploymentStatus.EmployedPrivateSector]: 'Employed Private Sector',
  [EmploymentStatus.EmployedGovernment]: 'Employed in Government',
  [EmploymentStatus.EmployedBPO]: 'Employed in BPO',
  [EmploymentStatus.EmployedOFW]: 'Employed as OFW',
  [EmploymentStatus.SelfEmployed]: 'Self-employed',
  [EmploymentStatus.ForeignResident]: 'Foreign Resident',
  [EmploymentStatus.Unemployed]: 'Unemployed',
  [EmploymentStatus.Retired]: 'Retired',
};

export const EMPLOYMENT_TENURE_VALUES = [
  'Less than 1 year',
  '1-2 years',
  '2-3 years',
  'More than 3 years',
];

export const CC_OWNER_TENURE_VALUES = [
  'Less than 6 months',
  '6 to 8 months',
  '9 to 11 months',
  'More than 1 year',
];

export const BUSINESS_OPERATING_DURATION_VALUES = [
  'Less than 1 year',
  '1-2 years',
  '2-3 years',
  '3 years or more',
];

export const LOAN_TERM_VALUES: Record<string, string> = {
  '12': '12 months',
  '24': '24 months',
  '36': '36 months',
  '48': '48 months',
  '60': '60 months',
};

export const LOAN_TENURE_VALUES_ALTL: Record<string, string> = {
  '1': '1 month',
  '2': '2 months',
  '3': '3 months',
  '4': '4 months',
  '5': '5 months',
  '6': '6 months',
  '7': '7 months',
  '8': '8 months',
  '9': '9 months',
  '10': '10 months',
  '11': '11 months',
  '12': '12 months',
};

export const LOAN_TENURE_VALUES_PL: Record<string, string> = {
  '12': '12 months',
  '18': '18 months',
  '24': '24 months',
  '36': '36 months',
  '48': '48 months',
  '60': '60 months',
};

type RangeValue = {
  minimum?: number;
  maximum?: number;
};

type additionalAttributeType = {
  prefix?: string;
  suffix?: string;
  emptyValueText?: string;
  value?: number | string;
  label?: string;
  attributeType?: string;
  attributeFormat: string;
  additionalInformation?: string;
  fees?: {
    annualFlatRate?: number;
    apr?: number;
    monthlyFlatRate?: number;
  }[];
  rangeValue?: RangeValue;
};

export const AuthForm: React.FC<AuthFormProps> = ({
  staticText,
  isOpen,
  setOpen,
  marketingConsent,
  onChangeMarketingConsent,
  onSubmit,
  currencyDisplay,
  currentCountryCode,
  tracking,
  clippedTextStaticText,
  productUid,
  attributes,
  providers,
  vehiclesData,
  hasReward = true,
}) => {
  const { brand } = useClientContext();
  const verticalCode = productUid?.split('.')[1] || 'CC';
  const isEastWest = productUid?.startsWith('PH.PL.EW.');
  const isPHAltLending = productUid?.startsWith('PH.ALTL.');
  const isBbbShortForm = isBbbShortFormProduct(productUid);
  // CORE-4728 items 1 and 3: the BBB template specifies max 40 and a
  // letters/spaces/-/' charset for first and last name. Every other vertical
  // keeps the letters-and-spaces-only sanitizer it shipped with, so this stays
  // scoped rather than becoming a silent behaviour change across the form.
  // Note the asymmetry this leaves inside the BBB template itself: middle name
  // (CORE-4532) accepts accented letters via p{L}, first/last do not, so
  // "Pena" survives and "Pena" with a tilde does not. Pre-existing; raised in
  // the PR rather than changed here.
  const nameCharsetPattern = isBbbShortForm ? /[^a-zA-Z '-]/g : /[^a-zA-Z ]/g;
  const nameMaxLength = isBbbShortForm ? 40 : undefined;
  const [vehicleMakers] = React.useState<string[]>(
    verticalCode === 'CL' ? vehiclesData?.makeNames || [] : [],
  );
  const [vehicleModels, setVehicleModels] = React.useState<VehicleModel[]>([]);
  const [carMake, setCarMake] = React.useState<string>('');
  React.useEffect(() => {
    if (carMake === '') return;
    const fetchVehicles = async (makes: string) => {
      const vehicleClient = getVehicleDomainService(brand);
      const vehicles = await vehicleClient.getVehiclesData({
        makeName: makes,
        useBolttechData: false,
      });
      setVehicleModels(vehicles || []);
    };
    fetchVehicles(carMake);
  }, [carMake, brand]);

  const [isSubmitting, setSubmitting] = React.useState<boolean>(false);

  const [isFormValid, setFormValid] = React.useState<boolean>(false);
  const [firstName, setFirstName] = React.useState<string | null>(null);
  const [middleName, setMiddleName] = React.useState<string | null>(null);
  const [noMiddleName, setNoMiddleName] = React.useState<boolean>(false);
  const [lastName, setLastName] = React.useState<string | null>(null);
  const [isEmailValid, setEmailValid] = React.useState<boolean>(false);
  const [isPhoneNumberValid, setPhoneNumberValid] =
    React.useState<boolean>(false);
  const [isLoanAmountValid, setLoanAmountValid] =
    React.useState<boolean>(false);
  const [isLoanTenureValid, setLoanTenureValid] =
    React.useState<boolean>(false);
  const [isBusinessOperatingDuration, setIsBusinessOperatingDuration] =
    React.useState<boolean>(false);
  const [ccTenureValid, setCCTenureValid] = React.useState<boolean>(false);
  const [isMonthlyIncomeValid, setMonthlyIncomeValid] =
    React.useState<boolean>(false);
  const [isEmploymentStatus, setIsEmploymentStatus] =
    React.useState<boolean>(false);
  const [isLoanTermValid, setLoanTermValid] = React.useState<boolean>(false);
  const [isEmploymentTenureValid, setEmploymentTenureValid] =
    React.useState<boolean>(false);
  const [errorFields, setErrorFields] = React.useState<string[]>([]);
  const [eastWestConsent, setEastWestConsent] = React.useState<boolean>(false);
  const [vehicleMakeValid, setVehicleMakeValid] =
    React.useState<boolean>(false);
  const [vehicleModelValid, setVehicleModelValid] =
    React.useState<boolean>(false);
  const [hasExistingCC, setHasExistingCC] = React.useState<string>('');
  const [valueLoanTenure, setValueLoanTenure] = React.useState<number | null>(
    null,
  );
  const [selectedBanks, setSelectedBanks] = React.useState<string[]>([]);
  const modalRef = React.useRef<HTMLInputElement>(null);
  const { value, onChange } = useMonthlyIncomeMask({ currencyDisplay });
  const { valueLoanAmount, onChangeLoanAmount } = useLoanAmountMask({
    currencyDisplay,
  });
  const dateFieldRef = React.useRef<HTMLInputElement>(null);

  const minAnnualIncome = attributes?.find(
    (item) => item?.attributeType === 'MINIMUM_ANNUAL_INCOME',
  ) as additionalAttributeType;
  const minimumLoanAmount = attributes?.find(
    (item) => item?.attributeType === 'MINIMUM_LOAN_AMOUNT',
  ) as additionalAttributeType;
  const maximumLoanAmount = attributes?.find(
    (item) => item?.attributeType === 'MAXIMUM_LOAN_AMOUNT',
  ) as additionalAttributeType;
  const minLoanTenure = attributes?.find(
    (item) => item?.attributeType === 'MINIMUM_LOAN_TENURE',
  ) as additionalAttributeType;
  const maxLoanTenure = attributes?.find(
    (item) => item?.attributeType === 'MAXIMUM_LOAN_TENURE',
  ) as additionalAttributeType;
  const minAge = attributes?.find(
    (item) => item?.attributeType === 'MINIMUM_AGE',
  ) as additionalAttributeType;
  const maxAge = attributes?.find(
    (item) => item?.attributeType === 'MAXIMUM_AGE',
  ) as additionalAttributeType;

  React.useEffect(() => {
    function clickOutside(e: MouseEvent) {
      if (
        isOpen &&
        modalRef.current &&
        !modalRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        trackQuitLeadDetailsOverlay(tracking);
      }
    }

    window.addEventListener('mousedown', clickOutside);
    return () => {
      window.removeEventListener('mousedown', clickOutside);
    };
  }, [isOpen, tracking, setOpen]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event,
  ) => {
    if (!isFormValid) {
      event.currentTarget.reportValidity();
      return;
    }
    setSubmitting(true);
    try {
      const entries = parseForm(
        event.currentTarget,
      ) as unknown as AuthFormValues;
      const phoneNumber = `09${String(entries.phoneNumber).replace(/\D/g, '')}`;
      entries.bankIssued = (
        new FormData(event.currentTarget).getAll(
          'bankIssued',
        ) as FormDataEntryValue[]
      ).map(String);
      // The middle name input is disabled (excluded from FormData) when the user
      // ticks "I don't have a Middle Name", so source it from state instead: "N/A"
      // when ticked, otherwise the typed value.
      // Trim the typed value so stray leading/trailing spaces aren't sent and a
      // whitespace-only entry collapses to '' (which the validity gate rejects).
      entries.middleName = noMiddleName
        ? 'N/A'
        : String(entries.middleName ?? '').trim();

      const sanitize = (val: string | number | undefined | null): number =>
        Number(String(val ?? '').replace(/\D/g, ''));
      const sanitizedLoanAmount = sanitize(entries.loanAmount);
      const sanitizedVehicleUnitPrice = sanitize(entries.vehicleUnitPrice);

      trackApplyLeadFormSubmit({
        ...tracking,
        phoneNumber,
        email: entries.email,
        firstName: entries.firstName,
        lastName: entries.lastName,
        existingCreditCard: entries.hasExistingCC,
        country: currentCountryCode,
        monthlyIncome: entries.monthlyIncome,
        employmentStatus: entries.employmentStatus,
        CCTenure: entries.CCTenure,
        bankIssued: entries.bankIssued,
        loanAmount: sanitizedVehicleUnitPrice || sanitizedLoanAmount || 0,
        loanTenure: Number(entries.loanTerm ?? entries.loanTenure ?? undefined),
        businessOperatingDuration: entries.businessOperatingDuration,
        vertical: verticalCode,
      });

      await onSubmit({
        ...entries,
        phoneNumber,
        marketingConsent,
        monthlyIncome: Number(
          // eslint-disable-next-line no-useless-escape
          String(entries.monthlyIncome).replace(/[^\d]/g, ''),
        ),
        loanAmount: sanitizedVehicleUnitPrice || sanitizedLoanAmount || 0,
        loanTenure: entries.loanTerm ?? entries.loanTenure ?? undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderMarketingConsent = ({ id }: { id: string }) => (
    <div className="flex flex-row items-start gap-[--gap-2]">
      <input
        type="checkbox"
        id={id}
        name="marketingConsent"
        className="cursor-pointer rounded-[--roundness-s] checked:accent-[--buttons-primary]"
        checked={marketingConsent}
        onChange={(e) => {
          onChangeMarketingConsent(!marketingConsent);
          trackApplyLeadFormInteract({
            componentType: 'checkbox',
            componentTitle: 'Lead Detail_Marketing Consent',
            fieldValue: e.target.checked ? 'true' : 'false',
            action: e.target.checked ? 'check' : 'uncheck',
            ...tracking,
          });
        }}
      />

      <div>
        <label
          htmlFor={id}
          className="mhg-alpha-body-2 cursor-pointer text-[--text-primary]"
        >
          {id === 'marketingConsentMobile' ? (
            <ClippedText clippedLength={40} staticText={clippedTextStaticText}>
              {staticText.dialogMarketingConsent}
            </ClippedText>
          ) : (
            staticText.dialogMarketingConsent
          )}
        </label>
      </div>
    </div>
  );

  const customFormVieldVisibility = useClient(() =>
    (
      new URLSearchParams(window.location.search).getAll('form_fields') || []
    ).map((val) => val.toLowerCase()),
  );

  const invalidFormFieldParams =
    customFormVieldVisibility?.filter(
      (val) => !Object.values(FORM_FIELD_NAME_TO_PARAM_MAP).includes(val),
    ) || [];

  const allParamsInvalid =
    customFormVieldVisibility?.sort().join(',') ===
    invalidFormFieldParams.sort().join(',');

  if (invalidFormFieldParams?.length) {
    console.warn(
      `Found invalid form field params: ${invalidFormFieldParams.join(', ')}`,
    );
  }

  const shouldShowOptionalField = useCallback(
    (field: keyof AuthFormValues) => {
      // CORE-4728 Part B: the BBB short form collects first/middle/last name,
      // mobile, email, the optional marketing consent and the T&C line — and
      // nothing else. Checked ahead of the `form_fields` query-string override
      // so the short form cannot be widened from a URL into qualification
      // fields this vertical has no branch for.
      if (isBbbShortForm) return false;
      if (
        !customFormVieldVisibility ||
        !customFormVieldVisibility.length ||
        allParamsInvalid
      ) {
        if (verticalCode === 'PL') {
          return !!FORM_FIELD_PL[field];
        }
        if (verticalCode === 'LFI') {
          return !!FORM_FIELD_LFI[field];
        }
        if (verticalCode === 'BL') {
          return !!FORM_FIELD_BL[field];
        }
        if (verticalCode === 'CL') {
          return !!FORM_FIELD_CL[field];
        }
        if (verticalCode === 'ALTL') {
          return !!FORM_FIELD_ALTL[field];
        }
        return !!FORM_FIELD_CC[field];
      }
      return (customFormVieldVisibility || []).includes(
        FORM_FIELD_NAME_TO_PARAM_MAP[field] || '',
      );
    },
    [customFormVieldVisibility, allParamsInvalid, verticalCode, isBbbShortForm],
  );

  React.useEffect(() => {
    const validations: Record<string, boolean> = {
      firstName: Boolean(firstName),
      // trim() so a whitespace-only middle name (the pattern allows \s) doesn't
      // satisfy the mandatory gate.
      middleName: noMiddleName || Boolean(middleName?.trim()),
      lastName: Boolean(lastName),
      email: isEmailValid,
      phoneNumber: isPhoneNumberValid,
      dateOfBirth: !(
        shouldShowOptionalField('dateOfBirth') &&
        dateFieldRef.current?.value === ''
      ),
      hasExistingCC: !(
        shouldShowOptionalField('hasExistingCC') && !hasExistingCC
      ),
      monthlyIncome: !(
        shouldShowOptionalField('monthlyIncome') && !isMonthlyIncomeValid
      ),
      employmentStatus: !(
        shouldShowOptionalField('employmentStatus') && !isEmploymentStatus
      ),
      bankIssued: !(
        shouldShowOptionalField('hasExistingCC') &&
        shouldShowOptionalField('bankIssued') &&
        hasExistingCC === ExistingCreditCard.Yes &&
        selectedBanks.length === 0
      ),
      ccTenure: !(
        shouldShowOptionalField('hasExistingCC') &&
        shouldShowOptionalField('CCTenure') &&
        hasExistingCC === ExistingCreditCard.Yes &&
        !ccTenureValid
      ),
      businessOperatingDuration: !(
        shouldShowOptionalField('businessOperatingDuration') &&
        !isBusinessOperatingDuration
      ),
      loanAmount: !(
        shouldShowOptionalField('loanAmount') && !isLoanAmountValid
      ),
      loanTenure: !(
        shouldShowOptionalField('loanTenure') && !isLoanTenureValid
      ),
      loanTerm: !(shouldShowOptionalField('loanTerm') && !isLoanTermValid),
      employmentTenure: !(
        shouldShowOptionalField('employmentTenure') && !isEmploymentTenureValid
      ),
      vehicleMake: !(
        shouldShowOptionalField('vehicleMake') && !vehicleMakeValid
      ),
      vehicleModel: !(
        shouldShowOptionalField('vehicleModel') && !vehicleModelValid
      ),
      eastWestConsent: !(isEastWest && !eastWestConsent),
    };

    const invalidFields = Object.keys(validations).filter(
      (field) => !validations[field],
    );

    setErrorFields((prev) =>
      prev.join(',') === invalidFields.join(',') ? prev : invalidFields,
    );

    setFormValid(invalidFields.length === 0);
  }, [
    firstName,
    middleName,
    noMiddleName,
    lastName,
    isEmailValid,
    isPhoneNumberValid,
    isMonthlyIncomeValid,
    dateFieldRef,
    isLoanAmountValid,
    isLoanTenureValid,
    hasExistingCC,
    ccTenureValid,
    isEmploymentStatus,
    selectedBanks,
    isBusinessOperatingDuration,
    isEmploymentTenureValid,
    isLoanTermValid,
    vehicleModelValid,
    vehicleMakeValid,
    shouldShowOptionalField,
    eastWestConsent,
    isEastWest,
  ]);

  const dialogTitleMap: Record<string, string> = {
    PL: staticText.dialogTitlePL,
    CL: staticText.dialogTitlePL,
    ALTL: staticText.dialogTitlePL,
    LFI: staticText.dialogTitleINS,
  };
  const defaultDialogTitle = hasReward
    ? staticText.dialogTitle
    : staticText.dialogTitleNoReward;

  return (
    <div
      data-dialog-backdrop="animated-modal"
      data-dialog-backdrop-close="true"
      // eslint-disable-next-line tailwindcss/classnames-order
      className={classNames(
        'fixed inset-0 z-[999] h-dvh w-screen bg-black/60 backdrop-blur-sm transition-opacity duration-300',
        { 'pointer-events-none opacity-0': !isOpen },
      )}
    >
      <div
        // eslint-disable-next-line tailwindcss/classnames-order
        className={classNames(
          'fixed inset-0 items-end justify-center max-w-screen transition-opacity duration-300',
          {
            'flex opacity-100': isOpen, // show modal
            'hidden opacity-0 pointer-events-none': !isOpen, // hide modal
          },
        )}
      >
        <div className="max-h-[80dvh] w-screen overflow-auto bg-black/50">
          <div
            ref={modalRef}
            className="mhg-alpha-box-shadow-2 flex w-full justify-center px-[--spacing-padding-x] py-[--spacing-padding-y] bg-[--fill-card]"
          >
            <LoaderForm
              onSubmit={handleSubmit}
              className="flex w-90 flex-col items-start justify-between gap-[--spacing-gap-y] font-[--font-font] md:w-[--general-narrow-content-max-width-3]"
            >
              <div className="mhg-alpha-title-1 text-[--text-primary]">
                {dialogTitleMap[verticalCode] || defaultDialogTitle}
              </div>
              <div className="grid w-full grid-cols-1 gap-[--spacing-gap-y] self-stretch md:grid-cols-3">
                <div className="flex w-full flex-col gap-1">
                  <div className="flex flex-row justify-between">
                    <div className="mhg-alpha-form-label">
                      {staticText.fieldFirstName.title}
                    </div>
                  </div>
                  <AlphaTextInput
                    // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                    className="!mhg-alpha-form-input flex items-center self-stretch !text-ellipsis"
                    type="text"
                    name="firstName"
                    maxLength={nameMaxLength}
                    placeholder={staticText.fieldFirstName.placeholder}
                    onValidate={(state) => {
                      if (!state.validity.valid) {
                        return staticText.fieldFirstName.invalid;
                      }
                    }}
                    required
                    onChange={(e, v) => {
                      const alphabetValue = e.target.value.replace(
                        nameCharsetPattern,
                        '',
                      );
                      setFirstName(alphabetValue);
                      e.target.value = alphabetValue;
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      clearTimeout((e.target as any).delayTimeout);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (e.target as any).delayTimeout = setTimeout(() => {
                        trackApplyLeadFormInteract({
                          componentType: 'textbox',
                          componentTitle: 'Lead Detail_First Name',
                          fieldValue: '<<PII redacted>>',
                          status: v.valid ? 'success' : 'failure',
                          ...tracking,
                        });
                      }, 300);
                    }}
                  />
                </div>

                <div className="flex w-full flex-col gap-1">
                  <div className="flex flex-row justify-between">
                    <div className="mhg-alpha-form-label">
                      {staticText.fieldMiddleName.title}
                    </div>
                  </div>
                  <AlphaTextInput
                    // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                    className="!mhg-alpha-form-input flex items-center self-stretch !text-ellipsis disabled:cursor-not-allowed disabled:bg-[--highlights-gray-light]"
                    type="text"
                    name="middleName"
                    value={middleName ?? ''}
                    disabled={noMiddleName}
                    required
                    pattern={staticText.fieldMiddleName.pattern}
                    maxLength={100}
                    indeterminate={noMiddleName || middleName == null}
                    placeholder={staticText.fieldMiddleName.placeholder}
                    onValidate={(state) => {
                      // The pattern allows \s, so a whitespace-only value (e.g.
                      // "   ") passes native validity. Flag it explicitly so the
                      // user gets feedback instead of a silently disabled CTA.
                      const value = String(state.value);
                      const whitespaceOnly =
                        value.length > 0 && value.trim() === '';
                      if (!state.validity.valid || whitespaceOnly) {
                        return staticText.fieldMiddleName.invalid;
                      }
                    }}
                    onChange={(e, v) => {
                      // Strip anything outside letters/marks/apostrophe/digits/
                      // space/hyphen. Literal space (not \s) so pasted tabs and
                      // Unicode whitespace don't survive into the value/payload.
                      const sanitizedValue = e.target.value.replace(
                        /[^\p{L}\p{M}'\d -]/gu,
                        '',
                      );
                      setMiddleName(sanitizedValue);
                      e.target.value = sanitizedValue;
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      clearTimeout((e.target as any).delayTimeout);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (e.target as any).delayTimeout = setTimeout(() => {
                        trackApplyLeadFormInteract({
                          componentType: 'textbox',
                          componentTitle: 'Lead Detail_Middle Name',
                          fieldValue: '<<PII redacted>>',
                          status: v.valid ? 'success' : 'failure',
                          ...tracking,
                        });
                      }, 300);
                    }}
                  />
                  <label className="mhg-alpha-body-2 mt-1 flex cursor-pointer flex-row items-center gap-[--gap-2] text-[--text-primary]">
                    <input
                      type="checkbox"
                      name="noMiddleName"
                      className="cursor-pointer rounded-[--roundness-s] checked:accent-[--buttons-primary]"
                      checked={noMiddleName}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        // Ticked: auto-fill "N/A" and disable the field. Unticked:
                        // clear it (null → indeterminate, editable again).
                        setNoMiddleName(checked);
                        setMiddleName(checked ? 'N/A' : null);
                        trackApplyLeadFormInteract({
                          componentType: 'checkbox',
                          componentTitle: 'Lead Detail_No Middle Name',
                          fieldValue: checked ? 'true' : 'false',
                          action: checked ? 'check' : 'uncheck',
                          ...tracking,
                        });
                      }}
                    />
                    {staticText.fieldMiddleName.noMiddleNameLabel}
                  </label>
                </div>

                <div className="flex w-full flex-col gap-1">
                  <div className="flex flex-row justify-between">
                    <div className="mhg-alpha-form-label">
                      {staticText.fieldLastName.title}
                    </div>
                  </div>
                  <AlphaTextInput
                    // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                    className="!mhg-alpha-form-input flex items-center self-stretch !text-ellipsis"
                    type="text"
                    name="lastName"
                    maxLength={nameMaxLength}
                    placeholder={staticText.fieldLastName.placeholder}
                    onValidate={(state) => {
                      if (!state.validity.valid) {
                        return staticText.fieldLastName.invalid;
                      }
                    }}
                    required
                    onChange={(e, v) => {
                      const alphabetValue = e.target.value.replace(
                        nameCharsetPattern,
                        '',
                      );
                      setLastName(alphabetValue);
                      e.target.value = alphabetValue;
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      clearTimeout((e.target as any).delayTimeout);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (e.target as any).delayTimeout = setTimeout(() => {
                        trackApplyLeadFormInteract({
                          componentType: 'textbox',
                          componentTitle: 'Lead Detail_Last Name',
                          fieldValue: '<<PII redacted>>',
                          status: v.valid ? 'success' : 'failure',
                          ...tracking,
                        });
                      }, 300);
                    }}
                  />
                </div>
              </div>

              <div className="grid w-full grid-cols-1 gap-[--spacing-gap-y] self-stretch md:grid-cols-3">
                <div className="flex w-full flex-col gap-1">
                  <div className="flex flex-col items-start self-stretch">
                    <div className="mhg-alpha-form-label flex gap-1">
                      <div>{staticText.fieldPhoneNumber.title}</div>
                    </div>
                  </div>
                  <AlphaTextInput
                    // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                    className="!mhg-alpha-form-input flex items-center self-stretch [appearance:textfield] read-only:bg-[--highlights-gray-light] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    type="text"
                    name="phoneNumber"
                    prefix="09"
                    placeholder={staticText.fieldPhoneNumber.placeholder}
                    pattern={staticText.fieldPhoneNumber.pattern}
                    maxLength={11}
                    onValidate={(state) => {
                      setPhoneNumberValid(state.validity.valid);
                      if (
                        (state.validity.patternMismatch &&
                          state.value !== '') ||
                        state.validity.valueMissing
                      ) {
                        return staticText.fieldPhoneNumber.invalid;
                      }
                    }}
                    required
                    onChange={(e, v) => {
                      e.target.value = phoneNumberMask(e.target.value);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      clearTimeout((e.target as any).delayTimeout);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (e.target as any).delayTimeout = setTimeout(() => {
                        trackApplyLeadFormInteract({
                          componentType: 'textbox',
                          componentTitle: 'Lead Detail_Phone Number',
                          fieldValue: '<<PII redacted>>',
                          status: v.valid ? 'success' : 'failure',
                          ...tracking,
                        });
                      }, 300);
                    }}
                  />
                </div>
                <div className="flex w-full flex-col gap-1">
                  <div className="flex flex-row justify-between">
                    <div className="mhg-alpha-form-label">
                      {staticText.fieldEmail.title}
                    </div>
                  </div>
                  <AlphaTextInput
                    // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                    className="!mhg-alpha-form-input flex items-center self-stretch !text-ellipsis"
                    type="email"
                    name="email"
                    placeholder={staticText.fieldEmail.placeholder}
                    pattern={staticText.fieldEmail.pattern}
                    onValidate={(state) => {
                      setEmailValid(state.validity.valid);
                      if (!state.validity.valid) {
                        return staticText.fieldEmail.invalid;
                      }
                    }}
                    required
                    onChange={(e, v) => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      clearTimeout((e.target as any).delayTimeout);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (e.target as any).delayTimeout = setTimeout(() => {
                        trackApplyLeadFormInteract({
                          componentType: 'textbox',
                          componentTitle: 'Lead Detail_Email',
                          fieldValue: '<<PII redacted>>',
                          status: v.valid ? 'success' : 'failure',
                          ...tracking,
                        });
                      }, 300);
                    }}
                  />
                  {isBbbShortForm && (
                    <div className="mhg-alpha-body-2 text-[--text-secondary]">
                      {staticText.fieldEmailApplicationLinkNote}
                    </div>
                  )}
                </div>

                {shouldShowOptionalField('dateOfBirth') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldDateOfBirth.title}
                      </div>
                    </div>
                    <AlphaTextInput
                      // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                      className="!mhg-alpha-form-input !text-ellipsis"
                      name="dateOfBirth"
                      type="date"
                      required
                      ref={dateFieldRef}
                      onClick={() => {
                        dateFieldRef.current?.showPicker?.();
                        dateFieldRef.current?.focus();
                      }}
                      onValidate={(state) => {
                        if (!state.value) return;
                        const dob = new Date(state.value);
                        const today = new Date();
                        const age = today.getFullYear() - dob.getFullYear();
                        const m = today.getMonth() - dob.getMonth();
                        const isBirthdayPassed =
                          m > 0 ||
                          (m === 0 && today.getDate() >= dob.getDate());
                        const realAge = isBirthdayPassed ? age : age - 1;
                        if (
                          (minAge && realAge < Number(minAge?.value)) ||
                          (maxAge && realAge > Number(maxAge?.value))
                        ) {
                          return staticText.fieldDateOfBirth.invalid;
                        }
                      }}
                    />
                  </div>
                )}

                {shouldShowOptionalField('monthlyIncome') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldMonthlyIncome.title}
                      </div>
                    </div>
                    <AlphaTextInput
                      // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                      className="!mhg-alpha-form-input flex items-center self-stretch !text-ellipsis"
                      name="monthlyIncome"
                      required
                      placeholder={(
                        t(staticText.fieldMonthlyIncome.placeholder, {
                          minMonthlyIncome: (minAnnualIncome?.value
                            ? Math.floor(Number(minAnnualIncome.value) / 12)
                            : 15000
                          ).toLocaleString('en-US'),
                        }) as string[]
                      ).join('')}
                      value={value}
                      onChange={(e) => {
                        // eslint-disable-next-line no-useless-escape
                        e.target.value = e.target.value.replace(/[^\d]/g, '');
                        onChange(e.target.value);
                      }}
                      onValidate={(state) => {
                        const valueNum = Number(
                          // eslint-disable-next-line no-useless-escape
                          String(state.value)
                            .replace(/\D/g, '')
                            .replace(/\./g, ''),
                        );
                        const minMonthlyIncome = minAnnualIncome?.value
                          ? Math.floor(Number(minAnnualIncome.value) / 12)
                          : 15000;
                        if (state.value && valueNum < minMonthlyIncome) {
                          setMonthlyIncomeValid(false);
                          return (
                            t(staticText.fieldMonthlyIncome.invalid, {
                              minMonthlyIncome:
                                Number(minMonthlyIncome).toLocaleString(
                                  'en-US',
                                ),
                            }) as string[]
                          ).join('');
                        }
                        setMonthlyIncomeValid(state.validity.valid);
                        if (!state.validity.valid) {
                          return (
                            t(staticText.fieldMonthlyIncome.invalid, {
                              minMonthlyIncome:
                                Number(minMonthlyIncome).toLocaleString(
                                  'en-US',
                                ),
                            }) as string[]
                          ).join('');
                        }
                      }}
                    />
                  </div>
                )}

                {shouldShowOptionalField('employmentStatus') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldEmploymentStatus.title}
                      </div>
                    </div>
                    <AlphaSelect
                      className="mhg-alpha-select-root"
                      name="employmentStatus"
                      required
                      menuProps={{
                        className: 'h-[250px] overflow-auto',
                      }}
                      selected={(element) => {
                        if (!element) {
                          return (
                            <span className="flex items-center gap-2 truncate text-gray-700 alpha-form-input">
                              {staticText.fieldEmploymentStatus.placeholder}
                            </span>
                          );
                        }
                        return React.cloneElement(element, {
                          className:
                            'text-[--text-primary] alpha-form-input pointer-events-none p-0 m-0 list-none truncate',
                        });
                      }}
                      onChange={(fieldValue = '') => {
                        trackApplyLeadFormInteract({
                          componentType: 'dropdown',
                          componentTitle: 'Lead Detail_Occupation',
                          fieldValue:
                            EMPLOYMENT_STATUS_VALUE_TO_LABEL[
                              fieldValue as EmploymentStatus
                            ] ?? fieldValue,
                          ...tracking,
                          action: 'select',
                          status: 'success',
                        });
                      }}
                      onValidate={(e) => {
                        setIsEmploymentStatus(e.valid);
                        if (!e.valid) {
                          return staticText.fieldEmploymentStatus.invalid;
                        }
                      }}
                    >
                      {EMPLOYMENT_STATUS_VALUES.map((option) => (
                        <Option
                          className="text-[--text-primary] alpha-form-input"
                          value={option}
                          key={option}
                        >
                          {EMPLOYMENT_STATUS_VALUE_TO_LABEL[option]}
                        </Option>
                      ))}
                    </AlphaSelect>
                  </div>
                )}

                {shouldShowOptionalField('employmentTenure') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldEmploymentTenure.title}
                      </div>
                    </div>
                    <AlphaSelect
                      className="mhg-alpha-select-root"
                      name="employmentTenure"
                      required
                      selected={(element) => {
                        if (!element) {
                          return (
                            <span className="flex items-center gap-2 text-gray-700 alpha-form-input">
                              {staticText.fieldEmploymentTenure.placeholder}
                            </span>
                          );
                        }
                        return React.cloneElement(element, {
                          className:
                            'text-[--text-primary] alpha-form-input pointer-events-none p-0 m-0 list-none truncate',
                        });
                      }}
                      onChange={(fieldValue = '') => {
                        trackApplyLeadFormInteract({
                          componentType: 'dropdown',
                          componentTitle: 'Lead Detail_Occupation',
                          fieldValue: fieldValue,
                          ...tracking,
                          action: 'select',
                          status: 'success',
                        });
                      }}
                      onValidate={(e) => {
                        setEmploymentTenureValid(e.valid);
                        if (!e.valid) {
                          return staticText.fieldEmploymentTenure.invalid;
                        }
                      }}
                    >
                      {EMPLOYMENT_TENURE_VALUES.map((option) => (
                        <Option
                          className="text-[--text-primary] alpha-form-input"
                          value={option}
                          key={option}
                        >
                          {option}
                        </Option>
                      ))}
                    </AlphaSelect>
                  </div>
                )}

                {shouldShowOptionalField('hasExistingCC') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex h-[--shapes-icon-size-md] flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldHasExistingCC.title}
                      </div>
                    </div>
                    <div className="flex h-10 items-center md:h-12">
                      <AlphaRadio
                        required={true}
                        name="hasExistingCC"
                        className="flex w-full flex-row"
                        containerClassname="w-1/2"
                        options={[
                          {
                            label:
                              EXISTING_CC_VALUE_TO_LABEL[
                                ExistingCreditCard.Yes
                              ],
                            value: ExistingCreditCard.Yes,
                          },
                          {
                            label:
                              EXISTING_CC_VALUE_TO_LABEL[ExistingCreditCard.No],
                            value: ExistingCreditCard.No,
                          },
                        ]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setHasExistingCC(e.target.value);
                        }}
                      />
                    </div>
                  </div>
                )}

                {shouldShowOptionalField('bankIssued') &&
                  providers &&
                  hasExistingCC === ExistingCreditCard.Yes && (
                    <div className="flex w-full flex-col gap-1">
                      <div className="flex flex-row justify-between">
                        <div className="mhg-alpha-form-label flex gap-1">
                          <span>{staticText.fieldBankIssued.title}</span>
                          <Tooltip
                            content={staticText.fieldBankIssued.tooltip}
                            placement="top"
                            className="mhg-alpha-caption max-w-50 rounded-lg shadow bg-white text-[--text-primary] text-sm"
                          >
                            <span className="ml-1 inline-flex h-fit items-center">
                              <Info className="inline-block size-shapes/icon-size-lg align-middle text-text/disabled" />
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                      <AlphaSelectMultiple
                        name="bankIssued"
                        required
                        className="mhg-alpha-select-root"
                        options={providers.map((p) => p.providerName)}
                        value={selectedBanks}
                        onChange={setSelectedBanks}
                        placeholder="Select bank"
                        onValidate={(e) => {
                          if (!e.valid) {
                            return staticText.fieldCCTenure.invalid;
                          }
                        }}
                      />
                    </div>
                  )}

                {shouldShowOptionalField('CCTenure') &&
                  hasExistingCC === ExistingCreditCard.Yes && (
                    <div className="flex w-full flex-col gap-1">
                      <div className="flex flex-row justify-between">
                        <div className="mhg-alpha-form-label flex gap-1">
                          <span>{staticText.fieldCCTenure.title}</span>
                          <Tooltip
                            content={staticText.fieldCCTenure.tooltip}
                            placement="top"
                            className="mhg-alpha-caption max-w-50 rounded-lg shadow bg-white text-[--text-primary] text-sm"
                          >
                            <span className="ml-1 inline-flex h-fit items-center">
                              <Info className="inline-block size-shapes/icon-size-lg align-middle text-text/disabled" />
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                      <AlphaSelect
                        className="mhg-alpha-select-root"
                        name="CCTenure"
                        required
                        selected={(element) => {
                          if (!element) {
                            return (
                              <span className="flex items-center gap-2 text-gray-700 alpha-form-input">
                                {staticText.fieldCCTenure.placeholder}
                              </span>
                            );
                          }
                          return React.cloneElement(element, {
                            className:
                              'text-[--text-primary] alpha-form-input pointer-events-none p-0 m-0 list-none truncate',
                          });
                        }}
                        onChange={(fieldValue = '') => {
                          trackApplyLeadFormInteract({
                            componentType: 'dropdown',
                            componentTitle:
                              'Lead Detail_Credit Card Ownership Tenure',
                            fieldValue: fieldValue,
                            ...tracking,
                            action: 'select',
                            status: 'success',
                          });
                        }}
                        onValidate={(e) => {
                          setCCTenureValid(e.valid);
                          if (!e.valid) {
                            return staticText.fieldCCTenure.invalid;
                          }
                        }}
                      >
                        {CC_OWNER_TENURE_VALUES.map((option) => (
                          <Option
                            className="text-[--text-primary] alpha-form-input"
                            value={option}
                            key={option}
                          >
                            {option}
                          </Option>
                        ))}
                      </AlphaSelect>
                    </div>
                  )}

                {shouldShowOptionalField('businessOperatingDuration') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldBusinessOperatingDuration.title}
                      </div>
                    </div>
                    <AlphaSelect
                      className="mhg-alpha-select-root"
                      name="businessOperatingDuration"
                      required
                      selected={(element) => {
                        if (!element) {
                          return (
                            <span className="flex items-center gap-2 text-gray-700 alpha-form-input">
                              {
                                staticText.fieldBusinessOperatingDuration
                                  .placeholder
                              }
                            </span>
                          );
                        }
                        return React.cloneElement(element, {
                          className:
                            'text-[--text-primary] alpha-form-input pointer-events-none p-0 m-0 list-none truncate',
                        });
                      }}
                      onChange={(fieldValue = '') => {
                        trackApplyLeadFormInteract({
                          componentType: 'dropdown',
                          componentTitle: 'Lead Detail_Occupation',
                          fieldValue: fieldValue,
                          ...tracking,
                          action: 'select',
                          status: 'success',
                        });
                      }}
                      onValidate={(e) => {
                        setIsBusinessOperatingDuration(e.valid);
                        if (!e.valid) {
                          return staticText.fieldBusinessOperatingDuration
                            .invalid;
                        }
                      }}
                    >
                      {BUSINESS_OPERATING_DURATION_VALUES.map((option) => (
                        <Option
                          className="text-[--text-primary] alpha-form-input"
                          value={option}
                          key={option}
                        >
                          {option}
                        </Option>
                      ))}
                    </AlphaSelect>
                  </div>
                )}
                {shouldShowOptionalField('loanAmount') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldLoanAmount.title}
                      </div>
                    </div>
                    <AlphaTextInput
                      // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                      className="!mhg-alpha-form-input flex items-center self-stretch !text-ellipsis"
                      name="loanAmount"
                      placeholder={(
                        t(staticText.fieldLoanAmount.placeholder, {
                          minLoanAmount: Number(
                            minimumLoanAmount?.value ?? 20000,
                          ).toLocaleString('en-US'),
                        }) as string[]
                      ).join('')}
                      value={valueLoanAmount}
                      onChange={(e) => {
                        // eslint-disable-next-line no-useless-escape
                        let numericValue = e.target.value.replace(/[^\d]/g, '');
                        if (
                          maximumLoanAmount?.value &&
                          Number(numericValue) > Number(maximumLoanAmount.value)
                        ) {
                          numericValue = maximumLoanAmount.value.toString();
                        }
                        onChangeLoanAmount(numericValue);
                      }}
                      onValidate={(state) => {
                        const valueNum = Number(
                          // eslint-disable-next-line no-useless-escape
                          String(state.value).replace(/[^\d]/g, ''),
                        );
                        const minimumLoanAmountValue =
                          minimumLoanAmount?.value ?? 20000;
                        const maximumLoanAmountValue =
                          maximumLoanAmount?.value ?? 2000000;
                        if (
                          state.value &&
                          valueNum < Number(minimumLoanAmountValue)
                        ) {
                          setLoanAmountValid(false);
                          return (
                            t(staticText.fieldLoanAmount.invalid, {
                              minLoanAmount: Number(
                                minimumLoanAmountValue,
                              ).toLocaleString('en-US'),
                            }) as string[]
                          ).join('');
                        }
                        if (
                          state.value &&
                          valueNum > Number(maximumLoanAmountValue)
                        ) {
                          setLoanAmountValid(false);
                          return (
                            t(staticText.fieldLoanAmount.invalidMax, {
                              maxLoanAmount: Number(
                                maximumLoanAmountValue,
                              ).toLocaleString('en-US'),
                            }) as string[]
                          ).join('');
                        }
                        setLoanAmountValid(state.validity.valid);
                        if (!state.validity.valid) {
                          return (
                            t(staticText.fieldLoanAmount.invalid, {
                              minLoanAmount: Number(
                                minimumLoanAmountValue,
                              ).toLocaleString('en-US'),
                            }) as string[]
                          ).join('');
                        }
                      }}
                      required
                    />
                  </div>
                )}

                {shouldShowOptionalField('loanTenure') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldLoanTenure.title}
                      </div>
                    </div>
                    {isPHAltLending ? (
                      <AlphaSelect
                        className="mhg-alpha-select-root"
                        name="loanTenure"
                        required
                        menuProps={{
                          className: 'h-[250px] overflow-auto',
                        }}
                        selected={(element) => {
                          if (!element) {
                            return (
                              <span className="flex min-w-0 items-center gap-2 text-gray-700 alpha-form-input">
                                <span className="block truncate">
                                  {staticText.fieldLoanTenure.selectPlaceholder}
                                </span>
                              </span>
                            );
                          }
                          return React.cloneElement(element, {
                            className:
                              'text-[--text-primary] alpha-form-input pointer-events-none p-0 m-0 list-none',
                          });
                        }}
                        onChange={(fieldValue = '') => {
                          trackApplyLeadFormInteract({
                            componentType: 'dropdown',
                            componentTitle: 'Lead Detail_Loan Tenure',
                            fieldValue:
                              LOAN_TENURE_VALUES_ALTL[
                                fieldValue as keyof typeof LOAN_TENURE_VALUES_ALTL
                              ] ?? fieldValue,
                            ...tracking,
                            action: 'select',
                            status: 'success',
                          });
                        }}
                        onValidate={(e) => {
                          setLoanTenureValid(e.valid);
                          if (!e.valid) {
                            return staticText.fieldLoanTenure.selectInvalid;
                          }
                        }}
                      >
                        {Object.entries(LOAN_TENURE_VALUES_ALTL).map(
                          ([value, label]) => (
                            <Option
                              className="text-[--text-primary] alpha-form-input"
                              value={value}
                              key={value}
                            >
                              {label}
                            </Option>
                          ),
                        )}
                      </AlphaSelect>
                    ) : verticalCode === 'PL' ? (
                      <AlphaSelect
                        className="mhg-alpha-select-root"
                        name="loanTenure"
                        required
                        menuProps={{
                          className: 'h-[250px] overflow-auto',
                        }}
                        selected={(element) => {
                          if (!element) {
                            return (
                              <span className="flex min-w-0 items-center gap-2 text-gray-700 alpha-form-input">
                                <span className="block truncate">
                                  {staticText.fieldLoanTenure.selectPlaceholder}
                                </span>
                              </span>
                            );
                          }
                          return React.cloneElement(element, {
                            className:
                              'text-[--text-primary] alpha-form-input pointer-events-none p-0 m-0 list-none',
                          });
                        }}
                        onChange={(fieldValue = '') => {
                          trackApplyLeadFormInteract({
                            componentType: 'dropdown',
                            componentTitle: 'Lead Detail_Loan Tenure',
                            fieldValue:
                              LOAN_TENURE_VALUES_PL[
                                fieldValue as keyof typeof LOAN_TENURE_VALUES_PL
                              ] ?? fieldValue,
                            ...tracking,
                            action: 'select',
                            status: 'success',
                          });
                        }}
                        onValidate={(e) => {
                          setLoanTenureValid(e.valid);
                          if (!e.valid) {
                            return staticText.fieldLoanTenure.selectInvalid;
                          }
                        }}
                      >
                        {Object.entries(LOAN_TENURE_VALUES_PL).map(
                          ([value, label]) => (
                            <Option
                              className="text-[--text-primary] alpha-form-input"
                              value={value}
                              key={value}
                            >
                              {label}
                            </Option>
                          ),
                        )}
                      </AlphaSelect>
                    ) : (
                      <AlphaTextInput
                        // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                        className="!mhg-alpha-form-input flex items-center self-stretch !text-ellipsis"
                        name="loanTenure"
                        maxLength={2}
                        suffix={valueLoanTenure === 1 ? ' month' : ' months'}
                        placeholder={(
                          t(staticText.fieldLoanTenure.invalid, {
                            minLoanTenure: minLoanTenure?.value ?? 6,
                          }) as string[]
                        ).join('')}
                        onChange={(e, v) => {
                          let numericValue = e.target.value
                            .replace(/[^\d]/g, '')
                            .slice(0, 2);
                          if (
                            maxLoanTenure?.value &&
                            Number(numericValue) > Number(maxLoanTenure.value)
                          ) {
                            numericValue = maxLoanTenure.value.toString();
                          }

                          e.target.value = numericValue;
                          setValueLoanTenure(Number(e.target.value));
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          clearTimeout((e.target as any).delayTimeout);
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (e.target as any).delayTimeout = setTimeout(() => {
                            trackApplyLeadFormInteract({
                              componentType: 'textbox',
                              componentTitle: 'Lead Detail_Loan Tenure',
                              fieldValue: e.target.value,
                              status: v.valid ? 'success' : 'failure',
                              ...tracking,
                            });
                          }, 300);
                        }}
                        onValidate={(state) => {
                          // eslint-disable-next-line no-useless-escape
                          state.value = String(state.value)
                            .replace(/[^\d]/g, '')
                            .slice(0, 2);
                          const valueNum = Number(state.value);
                          const minimumLoanTenure = minLoanTenure?.value ?? 6;
                          if (
                            valueNum &&
                            valueNum < Number(minimumLoanTenure)
                          ) {
                            setLoanTenureValid(false);
                            return (
                              t(staticText.fieldLoanTenure.invalid, {
                                minLoanTenure: minimumLoanTenure,
                              }) as string[]
                            ).join('');
                          }
                          setLoanTenureValid(state.validity.valid);
                          if (!state.validity.valid) {
                            return (
                              t(staticText.fieldLoanTenure.invalid, {
                                minLoanTenure: minimumLoanTenure,
                              }) as string[]
                            ).join('');
                          }
                        }}
                        required
                      />
                    )}
                  </div>
                )}

                {shouldShowOptionalField('vehicleMake') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldVehicleMake.title}
                      </div>
                    </div>
                    <AlphaSelect
                      className="mhg-alpha-select-root"
                      name="vehicleMake"
                      required
                      menuProps={{
                        className: 'h-[250px] overflow-auto',
                      }}
                      selected={(element) => {
                        if (!element) {
                          return (
                            <span className="flex items-center gap-2 text-gray-700 alpha-form-input">
                              {staticText.fieldVehicleMake.placeholder}
                            </span>
                          );
                        }
                        return React.cloneElement(element, {
                          className:
                            'text-[--text-primary] alpha-form-input pointer-events-none p-0 m-0 list-none truncate',
                        });
                      }}
                      onChange={(fieldValue = '') => {
                        setCarMake(fieldValue);
                        trackApplyLeadFormInteract({
                          componentType: 'dropdown',
                          componentTitle: 'Lead Detail_Vehicle Make',
                          fieldValue: fieldValue,
                          ...tracking,
                          action: 'select',
                          status: 'success',
                        });
                      }}
                      onValidate={(e) => {
                        setVehicleMakeValid(e.valid);
                        if (!e.valid) {
                          return staticText.fieldVehicleMake.invalid;
                        }
                      }}
                    >
                      {vehicleMakers.map((value) => (
                        <Option
                          className="text-[--text-primary] alpha-form-input"
                          value={value}
                          key={value}
                        >
                          {value}
                        </Option>
                      ))}
                    </AlphaSelect>
                  </div>
                )}

                {shouldShowOptionalField('vehicleModel') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldVehicleModel.title}
                      </div>
                    </div>
                    <AlphaSelect
                      className="mhg-alpha-select-root"
                      name="vehicleModel"
                      required
                      menuProps={{
                        className: 'h-[250px] overflow-auto',
                      }}
                      disabled={!carMake || vehicleModels.length === 0}
                      selected={(element) => {
                        if (!element) {
                          return (
                            <span className="flex items-center gap-2 text-gray-700 alpha-form-input">
                              {staticText.fieldVehicleModel.placeholder}
                            </span>
                          );
                        }
                        return React.cloneElement(element, {
                          className:
                            'text-[--text-primary] alpha-form-input pointer-events-none p-0 m-0 list-none truncate',
                        });
                      }}
                      onChange={(fieldValue = '') => {
                        trackApplyLeadFormInteract({
                          componentType: 'dropdown',
                          componentTitle: 'Lead Detail_Vehicle Model',
                          fieldValue: fieldValue,
                          ...tracking,
                          action: 'select',
                          status: 'success',
                        });
                      }}
                      onValidate={(e) => {
                        setVehicleModelValid(e.valid);
                        if (!e.valid) {
                          return staticText.fieldVehicleModel.invalid;
                        }
                      }}
                    >
                      {vehicleModels.map((model) => (
                        <Option
                          className="text-[--text-primary] alpha-form-input"
                          value={model.modelName}
                          key={model.modelId}
                        >
                          {model.modelName}
                        </Option>
                      ))}
                    </AlphaSelect>
                  </div>
                )}

                {shouldShowOptionalField('vehicleUnitPrice') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldVehicleUnitPrice.title}
                      </div>
                    </div>
                    <AlphaTextInput
                      // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                      className="!mhg-alpha-form-input flex items-center self-stretch !text-ellipsis"
                      name="vehicleUnitPrice"
                      placeholder={(
                        t(staticText.fieldVehicleUnitPrice.placeholder, {
                          vehicleMinUnitPrice: Number(
                            minimumLoanAmount?.value ?? 20000,
                          ).toLocaleString('en-US'),
                        }) as string[]
                      ).join('')}
                      value={valueLoanAmount}
                      onChange={(e) => {
                        // eslint-disable-next-line no-useless-escape
                        let numericValue = e.target.value.replace(/[^\d]/g, '');
                        if (
                          maximumLoanAmount?.value &&
                          Number(numericValue) > Number(maximumLoanAmount.value)
                        ) {
                          numericValue = maximumLoanAmount.value.toString();
                        }
                        onChangeLoanAmount(numericValue);
                      }}
                      onValidate={(state) => {
                        const valueNum = Number(
                          // eslint-disable-next-line no-useless-escape
                          String(state.value).replace(/[^\d]/g, ''),
                        );
                        const minimumLoanAmountValue =
                          minimumLoanAmount?.value ?? 20000;
                        const maximumLoanAmountValue =
                          maximumLoanAmount?.value ?? 2000000;
                        if (
                          state.value &&
                          valueNum < Number(minimumLoanAmountValue)
                        ) {
                          setLoanAmountValid(false);
                          return (
                            t(staticText.fieldVehicleUnitPrice.invalid, {
                              vehicleMinUnitPrice: Number(
                                minimumLoanAmountValue,
                              ).toLocaleString('en-US'),
                            }) as string[]
                          ).join('');
                        }
                        if (
                          state.value &&
                          valueNum > Number(maximumLoanAmountValue)
                        ) {
                          setLoanAmountValid(false);
                          return (
                            t(staticText.fieldVehicleUnitPrice.invalidMax, {
                              vehicleMaxUnitPrice: Number(
                                maximumLoanAmountValue,
                              ).toLocaleString('en-US'),
                            }) as string[]
                          ).join('');
                        }
                        setLoanAmountValid(state.validity.valid);
                        if (!state.validity.valid) {
                          return (
                            t(staticText.fieldVehicleUnitPrice.invalid, {
                              vehicleMinUnitPrice: Number(
                                minimumLoanAmountValue,
                              ).toLocaleString('en-US'),
                            }) as string[]
                          ).join('');
                        }
                      }}
                      required
                    />
                  </div>
                )}

                {shouldShowOptionalField('loanTerm') && (
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <div className="mhg-alpha-form-label">
                        {staticText.fieldLoanTerm.title}
                      </div>
                    </div>
                    <AlphaSelect
                      className="mhg-alpha-select-root"
                      name="loanTerm"
                      required
                      menuProps={{
                        className: 'h-[250px] overflow-auto',
                      }}
                      selected={(element) => {
                        if (!element) {
                          return (
                            <span className="flex min-w-0 items-center gap-2 text-gray-700 alpha-form-input">
                              <span className="block truncate">
                                {staticText.fieldLoanTerm.placeholder}
                              </span>
                            </span>
                          );
                        }
                        return React.cloneElement(element, {
                          className:
                            'text-[--text-primary] alpha-form-input pointer-events-none p-0 m-0 list-none',
                        });
                      }}
                      onChange={(fieldValue = '') => {
                        trackApplyLeadFormInteract({
                          componentType: 'dropdown',
                          componentTitle: 'Lead Detail_Loan Term',
                          fieldValue:
                            LOAN_TERM_VALUES[
                              fieldValue as keyof typeof LOAN_TERM_VALUES
                            ] ?? fieldValue,
                          ...tracking,
                          action: 'select',
                          status: 'success',
                        });
                      }}
                      onValidate={(e) => {
                        setLoanTermValid(e.valid);
                        if (!e.valid) {
                          return staticText.fieldLoanTerm.invalid;
                        }
                      }}
                    >
                      {Object.entries(LOAN_TERM_VALUES).map(
                        ([value, label]) => (
                          <Option
                            className="text-[--text-primary] alpha-form-input"
                            value={value}
                            key={value}
                          >
                            {label}
                          </Option>
                        ),
                      )}
                    </AlphaSelect>
                  </div>
                )}
              </div>

              {isEastWest && (
                <div className="flex flex-row items-start gap-[--gap-2]">
                  <input
                    type="checkbox"
                    id="eastWestConsent"
                    className="cursor-pointer rounded-[--roundness-s] checked:accent-[--buttons-primary]"
                    checked={eastWestConsent}
                    onChange={() => setEastWestConsent(!eastWestConsent)}
                  />
                  <label
                    htmlFor="eastWestConsent"
                    className="mhg-alpha-body-2 cursor-pointer text-[--text-primary]"
                  >
                    I have read and agree to EastWest Bank&apos;s{' '}
                    <a
                      href="https://www.eastwestbanker.com/help-and-support-center/loans/terms-and-contions-for-personal-loan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Terms and Conditions
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.eastwestbanker.com/privacy-statement"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>
              )}

              <div className="md:hidden">
                {renderMarketingConsent({ id: 'marketingConsentMobile' })}
              </div>

              <div className="hidden md:block">
                {renderMarketingConsent({ id: 'marketingConsentDesktop' })}
              </div>

              {isBbbShortForm && (
                <div className="mhg-alpha-body-2 w-full text-[--text-secondary]">
                  {staticText.dialogDataRetentionNote}
                </div>
              )}

              <div className="flex w-full flex-col items-center justify-start gap-[--spacing-gap-y] self-stretch md:flex-row">
                <div className="mhg-alpha-body-2 w-full text-[--text-secondary]">
                  {staticText.dialogTermsAndConditions}
                </div>

                <SubmitButton
                  type="button"
                  className={classNames(
                    'mhg-alpha-btn-primary w-full gap-[--button-gap] !rounded-[--radius-button] md:w-[--general-3-col-width]',
                    {
                      'bg-[#BFDCEB] text-white/70 hover:text-white/70 active:text-white/70 hover:bg-[#BFDCEB] active:bg-[#BFDCEB]':
                        isSubmitting || !isFormValid,
                    },
                  )}
                  disabled={isSubmitting}
                  onClick={() => {
                    if (!isFormValid) {
                      track({
                        event: 'error_event',
                        error_category: 'lead form',
                        error_type: 'validation',
                        error_message: `CTA disabled due to error in fields (${errorFields.join(', ')})`,
                        step_number: '1',
                        step_name: 'lead-submission',
                        vertical_code: verticalCode,
                        search_result_parameter: '',
                      });
                    }
                  }}
                >
                  {staticText.dialogSubmitBtn}
                </SubmitButton>
              </div>
            </LoaderForm>
          </div>
        </div>
      </div>
    </div>
  );
};

function phoneNumberMask(value: string): string {
  // Remove all non-numeric characters
  let numericValue = value.replace(/\D/g, '');

  // Limit to max 9 characters
  numericValue = numericValue.slice(0, 9);

  // Format as xx xxx xxxx
  if (numericValue.length <= 5) {
    return numericValue;
  } else {
    return `${numericValue.slice(0, 2)} ${numericValue.slice(2, 5)} ${numericValue.slice(5, 9)}`;
  }
}
